import type { LoomAuthoringDocument, LoomSceneNode } from "./loomAuthoringModel";

export type LoomHierarchyEdgeKind = "root" | "input" | "detached";
export type LoomHierarchyReferenceKind =
  | "owned"
  | "shared-reference"
  | "cycle-reference"
  | "missing-reference";

export interface LoomSceneHierarchyItem {
  children: readonly LoomSceneHierarchyItem[];
  depth: number;
  edgeKind: LoomHierarchyEdgeKind;
  entityKind: "authored" | "derived";
  firstPath: string;
  inputIndex: number | null;
  key: string;
  node: LoomSceneNode | null;
  nodeId: string;
  parentNodeId: string | null;
  path: string;
  referenceKind: LoomHierarchyReferenceKind;
  sourceNodeId: string | null;
}

export function loomSceneNodeInputs(node: LoomSceneNode): readonly string[] {
  if (node.type === "transform") return [node.inputNodeId];
  if (node.type === "boolean") return node.inputNodeIds;
  return [];
}

export function deriveLoomSceneHierarchy(
  document: LoomAuthoringDocument,
): readonly LoomSceneHierarchyItem[] {
  const byId = new Map(document.nodes.map((node) => [node.id, node]));
  const firstPaths = new Map<string, string>();

  const walk = (
    nodeId: string,
    edgeKind: LoomHierarchyEdgeKind,
    parentNodeId: string | null,
    inputIndex: number | null,
    path: string,
    stack: ReadonlySet<string>,
  ): LoomSceneHierarchyItem => {
    const node = byId.get(nodeId) ?? null;
    const sourceNodeId = node?.type === "transform" && node.operation.transform === "mirror"
      ? node.inputNodeId
      : null;
    const entityKind = sourceNodeId ? "derived" as const : "authored" as const;
    const firstPath = firstPaths.get(nodeId);
    if (!node) return {
      children: [], depth: stack.size + 1, edgeKind, entityKind, firstPath: path,
      inputIndex, key: path, node, nodeId, parentNodeId, path,
      referenceKind: "missing-reference", sourceNodeId,
    };
    if (stack.has(nodeId)) return {
      children: [], depth: stack.size + 1, edgeKind, entityKind,
      firstPath: firstPath ?? path, inputIndex, key: path, node, nodeId,
      parentNodeId, path, referenceKind: "cycle-reference", sourceNodeId,
    };
    if (firstPath) return {
      children: [], depth: stack.size + 1, edgeKind, entityKind, firstPath,
      inputIndex, key: path, node, nodeId, parentNodeId, path,
      referenceKind: "shared-reference", sourceNodeId,
    };

    firstPaths.set(nodeId, path);
    const nextStack = new Set(stack);
    nextStack.add(nodeId);
    return {
      children: loomSceneNodeInputs(node).map((childId, childIndex) => walk(
        childId,
        "input",
        nodeId,
        childIndex,
        `${path}/input:${childIndex}:${childId}`,
        nextStack,
      )),
      depth: stack.size + 1,
      edgeKind,
      entityKind,
      firstPath: path,
      inputIndex,
      key: path,
      node,
      nodeId,
      parentNodeId,
      path,
      referenceKind: "owned",
      sourceNodeId,
    };
  };

  const result = document.rootNodeIds.map((nodeId, index) => walk(
    nodeId, "root", null, index, `root:${index}:${nodeId}`, new Set(),
  ));
  document.nodes.forEach((node, index) => {
    if (firstPaths.has(node.id)) return;
    result.push(walk(
      node.id, "detached", null, index, `detached:${index}:${node.id}`, new Set(),
    ));
  });
  return result;
}
